import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Icon, Dropdown, Checkbox, Menu, Button } from 'semantic-ui-react';
import { useTextResources } from '../../language';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { DatePicker } from '../../components/shared/DatePicker';

/**
 * CalendarToolbar component for controlling calendar navigation and view settings.
 * Example usage and structure referenced from Scheduler in scheduler.jsx.
 *
 * @param {Object} props
 * @param {string} title - Current calendar title
 * @param {string} defaultView - Current view type
 * @param {Function} onPrev - Callback for previous navigation
 * @param {Function} onNext - Callback for next navigation
 * @param {Function} onChangeView - Callback for changing view
 * @param {Object} viewSettings - Current view settings
 * @param {Function} onToggleSetting - Callback for toggling a view setting
 */

const CalendarToolbar = ({
    title,
    defaultView,
    onPrev,
    onNext,
    onChangeView,
    viewSettings,
    onToggleSetting,
    setSelectedHotels,
    onSetDate,
    hotels,
    onSetReservationIcon
}) => {
    const text = useTextResources();
    
    const isHebrew = window.user?.UserSettings?.[0]?.Language?.Code?.startsWith('he');
    
    return (
        <Grid columns='3' verticalAlign='middle'>
            <Grid.Row style={{ marginBottom: '.5rem' }}>
                <Grid.Column>
                    <HotelsDropDown
                        multiple
                        onChange={setSelectedHotels}
                        data={hotels}
                        placeholder={text("All")}
                        search
                        clearable
                        style={{ minWidth: '130px' }}
                    />
                </Grid.Column>
                <Grid.Column textAlign='center' className={`date-title ${isHebrew ? 'title-rtl' : 'title-ltr'}`}>
                    <Icon name='arrow left' link onClick={onPrev} fitted />
                    <DatePicker
                        className='calendar-datepicker'
                        id='dateinput'
                        type='basic'
                        label={title}
                        onChange={onSetDate}
                    />
                    {/* <div style={{ margin: '0 1rem', width: 300, display: 'inline-block' }}>{title}</div> */}
                    <Icon name='arrow right' link onClick={onNext} fitted />
                </Grid.Column>
                <Grid.Column textAlign='right'>
                    <Menu secondary compact>
                        <Dropdown
                            item
                            value={defaultView}
                            options={[
                                { value: 'resourceTimelineDay', key: 'day', text: text('Day') },
                                { value: 'resourceTimelineWeek', key: 'week', text: text('Week') },
                                { value: 'resourceTimelineBiWeek', key: 'biweek', text: text('TwoWeeks') },
                                { value: 'resourceTimelineSlidingMonth', key: 'current', text: text('Month') },
                                { value: 'resourceTimelineMonth', key: 'month', text: text('CurrentMonth') },
                            ]}
                            onChange={(e, data) => onChangeView(data.value)}
                        />
                        <Dropdown
                            item
                            value={viewSettings.reservationIcon || 'none'}
                            options={[
                                {
                                    value: 'none',
                                    key: 'none',
                                    text: text('Default'),
                                },
                                {
                                    value: '24h',
                                    key: '24h',
                                    text: text('Last24Hours'),
                                },
                                {
                                    value: 'balance',
                                    key: 'balance',
                                    text: text('PaymentStatus'),
                                },
                                {
                                    value: 'agent',
                                    key: 'agent',
                                    text: text('Agent'),
                                },
                                {
                                    value: 'company',
                                    key: 'company',
                                    text: text('Company'),
                                },
                                {
                                    value: 'confirmation',
                                    key: 'confirmation',
                                    text: text('Confirmation'),
                                },
                                {
                                    value: 'documents',
                                    key: 'documents',
                                    text: text('Documents'),
                                },
                            ]}
                            onChange={(e, { value }) => onSetReservationIcon(value)}
                        />
                        <Dropdown
                            direction='left'
                            item
                            text={text('ViewSettings')}
                            closeOnChange={false}
                            closeOnBlur={false}
                            options={[
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showYield}
                                            label={text('Yield')}
                                            onChange={() => onToggleSetting('showYield')}
                                        />
                                    ),
                                    key: 'Yield',
                                },
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showHoliday}
                                            label={text('Holidays')}
                                            onChange={() => onToggleSetting('showHoliday')}
                                        />
                                    ),
                                    key: 'Holidays',
                                },
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showComments}
                                            label={text('Comments')}
                                            onChange={() => onToggleSetting('showComments')}
                                        />
                                    ),
                                    key: 'Comments',
                                },
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showOccupancy}
                                            label={text('Occupancy')}
                                            onChange={() => onToggleSetting('showOccupancy')}
                                        />
                                    ),
                                    key: 'Occupancy',
                                },
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showHouseKeeping}
                                            label={text('Housekeeping')}
                                            onChange={() => onToggleSetting('showHouseKeeping')}
                                        />
                                    ),
                                    key: 'HouseKeeping',
                                },
                                {
                                    text: (
                                        <Checkbox
                                            checked={viewSettings.showReservationNumber}
                                            label={text('ReservationNumber')}
                                            onChange={() => onToggleSetting('showReservationNumber')}
                                        />
                                    ),
                                    key: 'ReservationNumber',
                                },
                            ]}
                        />
                    </Menu>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

CalendarToolbar.propTypes = {
    title: PropTypes.string,
    defaultView: PropTypes.string,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    onChangeView: PropTypes.func,
    viewSettings: PropTypes.object,
    onToggleSetting: PropTypes.func,
    onSetReservationIcon: PropTypes.func,
    setSelectedHotels: PropTypes.func,
    onSetDate: PropTypes.func,
    hotels: PropTypes.array,
};

export default CalendarToolbar;